import { themeHelper } from 'uicomponents';

import {
  AgendaItem,
  Booth,
  BoothTiers,
  Broadcast,
  DiscussionGroup,
  EventState,
  EventUser,
  SurveyIframeState,
  TicketType,
  TrackItem,
  User,
  Widget,
  LiveRunOfShowCue,
  getDefaultState,
  SlideNavigationAction,
  LayoutSettings
} from './types';

import type { AiSummaryStatusResponse } from '@goldcast/api/backend';

import { MATTERMOST_DEFAULTS } from '@/constants';
import i18n from '@/i18n';
import { saveValueToLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { SpeakerStageRequest } from '@/services/stageControl.service';
import { RootState } from '@/store';
import { createAccessibleThemeTokens } from '@/store/modules/brandKit/util';
import { makeUser } from '@/utils/userTransform';

const mutations = {
  setLastUberFetchEpoch(state: EventState, epoch: number) {
    state.lastUberFetchEpoch = epoch;
  },
  setSSOProcessing(state: EventState, value: null | Promise<any>) {
    loggerService.log('SSOLogin: Setting enter button loader value ', value);
    state.ssoprocessing = value;
  },
  setEmbeddedWidgets(state: EventState, widgets: Widget[]) {
    state.widgets = widgets?.filter(widget => widget.enabled) || [];
  },
  invalidateEventNetwork(state: EventState) {
    state.event.staleEventNetwork = true;
  },
  markEventNetworkValid(state: EventState) {
    state.event.staleEventNetwork = false;
  },
  setPrimaryBooths(state: EventState, booths: Record<string, BoothTiers>) {
    state.event.primaryBooths = booths;
  },
  setSecondaryBooths(state: EventState, booths: Record<string, BoothTiers>) {
    state.event.secondaryBooths = booths;
  },
  setBooths(state: EventState, booths: Booth[]) {
    state.event.booths = booths;
  },
  upsertBooth(state: EventState, booth: Booth) {
    let boothExists = false;
    const newBooths = state.event.booths?.map((it: Booth) => {
      if (it.id !== booth.id) {
        return it;
      }

      boothExists = true;
      return { ...it, ...booth };
    });
    if (!state.event.booths?.length || !boothExists) {
      newBooths.push(booth);
    }
    state.event.booths = newBooths;

    const { enable_booth_tiers } = state.event;

    booth.tier = enable_booth_tiers ? booth.tier : 'TIER1';

    const primaryUpdatedTier = {
      name: state.event.primaryBooths[booth.tier]?.name,
      booths: state.event.primaryBooths[booth.tier]?.booths?.concat(booth)
    };

    const secondaryUpdatedTier = {
      name: state.event.secondaryBooths[booth.tier]?.name,
      booths: state.event.secondaryBooths[booth.tier]?.booths?.concat(booth)
    };

    if (booth.booth_type === 'Primary') {
      state.event.primaryBooths = {
        ...state.event.primaryBooths,
        [booth.tier]: primaryUpdatedTier
      };
    } else if (booth.booth_type === 'Secondary') {
      state.event.secondaryBooths = {
        ...state.event.secondaryBooths,
        [booth.tier]: secondaryUpdatedTier
      };
    }
  },
  updateBoothsInPlace(state: EventState, booth: Partial<Booth>) {
    state.event.booths = state.event.booths.map((it: Booth) => {
      if (it.id !== booth.id) {
        return it;
      }
      return { ...it, ...booth };
    });

    const updatedPrimaryBooths: Record<string, BoothTiers> = {},
      updatedSecondaryBooths: Record<string, BoothTiers> = {};

    const TIERS = ['TIER1', 'TIER2', 'TIER3'];

    TIERS.forEach(tier => {
      updatedPrimaryBooths[tier] = {
        name: state.event.primaryBooths[tier]?.name,
        booths: []
      };

      updatedSecondaryBooths[tier] = {
        name: state.event.secondaryBooths[tier]?.name,
        booths: []
      };
    });

    const { enable_booth_tiers } = state.event;

    state.event.booths.map((it: Booth) => {
      const tier = enable_booth_tiers ? it.tier : 'TIER1';

      if (it.booth_type === 'Primary') {
        updatedPrimaryBooths[tier]?.booths?.push(it);
      } else {
        updatedSecondaryBooths[tier]?.booths?.push(it);
      }
    });

    state.event.primaryBooths = updatedPrimaryBooths;
    state.event.secondaryBooths = updatedSecondaryBooths;
  },
  setTracks(state: EventState, tracks: TrackItem[]) {
    state.event.tracks = tracks;
  },
  setDataLoaded(state: EventState, value: boolean) {
    state.event.dataLoaded = value;
  },
  setOrganization(state: EventState, value: any) {
    state.organization = value;
  },
  addUsersBulk(state: EventState, users: { [k: string]: User }) {
    // Do not delete existing users
    /*
    state.event = {
      ...state.event,
      users: Object.freeze({ ...state.event.users, ...users })
    };
   */

    // state.event.users = { ...state.event.users, ...users };
    /*
    if (Object.keys(state.event.users) < 500) {
      state.event.users = {
        ...state.event,
        users: { ...state.event.users, ...users }
      };
    } else {
    */
    for (const userid in users) {
      if (!state.event.users) {
        state.event.users = {};
      }
      Object.assign(state.event.users, { [userid]: users[userid] });
      if (users[userid].mmid) {
        Object.assign(state.event.users, { [`${users[userid].mmid}`]: users[userid] });
      }
    }
    //}
  },

  updateLocalLanguage(state: EventState, value: string) {
    i18n.global.locale.value = value;
    state.translator.selectedLocale = value;
    state.translator.lastUpdated = new Date();
  },

  updateScrollPosition(state: EventState, payload: { scrollPosition: number; id: string }) {
    const { id, scrollPosition } = payload;
    const broadcastId = state.event.agenda.broadcasts.find((b: Broadcast) => b.id === id);
    if (broadcastId) {
      broadcastId.slide_scroll_position = scrollPosition;
    }
  },

  addPartialUsersFromUber(state: EventState, payload: { users: any[]; event: string }) {
    const usersList: { [k: string]: Partial<User> } = state.event.users || {};
    payload.users.forEach(user => {
      const user_id = user.id;
      if (state.event.users[user_id]) {
        loggerService.info(`${user_id} was already in store, ignoring`);
      } else {
        loggerService.info(`${user_id} was not in store, Adding partial user`);
        const madeUser = makeUser({
          user,
          id: user.id,
          event: payload.event,
          blocked: false,
          view_as_attendee: false
        });
        usersList[user_id] = madeUser;
        usersList[madeUser.mmid] = madeUser;
      }
    });

    state.event = {
      ...state.event,
      users: usersList
    };
    state.userStateInvalidated = Date.now();
  },
  setEvent(state: EventState, payload: { event: any }) {
    const isMobile = window.mobileCheck && window.mobileCheck();
    const mobileConfigs = {
      drift_bot_enabled: false,
      drift_landing_as_default: false,
      drift_landing_enabled: false,
      resources: state.event.resource
    };
    const overrides = isMobile ? mobileConfigs : {};
    state.event = { ...state.event, ...payload.event, ...overrides };
    window.eventid = state.event.id;
    document.title = (payload.event && payload.event.title) || state.event?.title || 'Goldcast';
  },
  setPricingConfig(state: EventState, pricingConfig: Record<string, any>) {
    state.pricingConfig = pricingConfig;
  },
  setCurrentLiveAgendaIndex(state: EventState, index: Number) {
    state.event.currentLiveAgendaItemIndex = index;
  },
  setThemeStyles(state: EventState, rootState: RootState) {
    const { theme_obj = {} } = state.event || {};

    const legacyThemeStyles = themeHelper.themeObjToStyleVariables(
      theme_obj,
      state.event.is_hybrid,
      rootState.user.liteMode.liteModeActive,
      state.stage?.isLiveStreamMode
    );
    const hasBrandColors =
      theme_obj.theme_primary_color ||
      theme_obj.theme_secondary_color ||
      theme_obj.primary_text_color ||
      theme_obj.secondary_text_color;

    if (hasBrandColors) {
      const accessibleTokens = createAccessibleThemeTokens({
        primaryBackground: theme_obj.theme_primary_color,
        secondaryBackground: theme_obj.theme_secondary_color,
        primaryForeground: theme_obj.primary_text_color,
        secondaryForeground: theme_obj.secondary_text_color
      });
      state.themeStyles = {
        ...accessibleTokens,
        ...legacyThemeStyles
      };
    } else {
      state.themeStyles = legacyThemeStyles;
    }
    saveValueToLocalstore('themeStyles', state.themeStyles);
  },
  setDiscussionGroups(state: EventState, payload: { discussionGroups: DiscussionGroup[] }) {
    state.discussionGroups = payload.discussionGroups;
  },
  setIndividualDiscGroup(state: EventState, group: DiscussionGroup) {
    state.routeDiscussionGroup = group;
  },
  cleanState(state: EventState) {
    const defaultState = getDefaultState();
    if (state.ssoprocessing) {
      defaultState.ssoprocessing = state.ssoprocessing;
    }
    Object.assign(state, defaultState);
  },
  setDiscussionGroupUsers(state: EventState, { groupId, users }: { groupId: string; users: User[] }) {
    state.discussionGroupUsers = {
      ...state.discussionGroupUsers,
      [groupId]: users
    };
  },
  setTotalCount(state: EventState, count: number) {
    state.event.totalParticipants = count;
  },
  setFilteredNetworkUsers(state: EventState, participants: EventUser[]) {
    state.event.participants = participants;
  },
  setResources(state: EventState, resources: any[]) {
    state.event = {
      ...state.event,
      resources: resources.reduce((acc, resource) => {
        const key = resource.content_type === 'event' ? 'event' : `${resource.content_type}-${resource.object_id}`;

        if (acc[key]) {
          acc[key] = [...acc[key], resource];
        } else {
          acc[key] = [resource];
        }

        return acc;
      }, {})
    };
  },
  setEventAgenda(
    state: EventState,
    agenda: {
      items: AgendaItem[];
      broadcasts: Broadcast[];
    }[]
  ) {
    state.event = {
      ...state.event,
      agenda
    };
  },
  updateAgendaItem(state: EventState, item: AgendaItem) {
    try {
      const key = item.type == 'BROADCAST' ? 'broadcasts' : 'items';
      if (state.event?.agenda[key]) {
        state.event = {
          ...state.event,
          agenda: {
            ...state.event.agenda,
            [key]: state.event.agenda[key].map((b: AgendaItem) => {
              if (b.id !== item.id) {
                return b;
              }
              return item;
            })
          }
        };
      }
    } catch (err) {
      loggerService.error('Error updating agenda item', err);
    }
  },
  updateAgendaItemLoadingState(
    state: EventState,
    payload: { sessionId: string; sessionType: string; loadingKey: string; loadingState: boolean }
  ) {
    const { sessionId, loadingKey, loadingState } = payload;
    const { broadcasts = [], items = [] } = state.event.agenda || {};
    const agendaItems = broadcasts.concat(items);
    const item = agendaItems.find((item: AgendaItem) => {
      return item.id === sessionId;
    });
    Object.assign(item, { agenda_control_loading_state: { [loadingKey]: loadingState } });
    // Vue.set(item, 'agenda_control_loading_state', {
    //   ...item['agenda_control_loading_state'],
    //   [loadingKey]: loadingState
    // });
  },

  updateSimuliveVideos(state: EventState, { broadcastId, videos }: { broadcastId: string; videos: any }) {
    if (broadcastId && videos && state.event?.agenda?.broadcast) {
      state.event = {
        ...state.event,
        agenda: {
          ...state.event.agenda,
          broadcast: state.event.agenda.broadcast.map((b: Broadcast) => {
            if (b.id !== broadcastId) {
              return b;
            }
            return {
              ...b,
              simulive_videos: videos
            };
          })
        }
      };
    }
  },
  displayTicker(state: EventState, payload: { tickerText: string; tickerUrl: string; broadcastId: string }) {
    const broadcast = state.event.agenda.broadcasts.find(b => b.id === payload.broadcastId);
    if (broadcast) {
      broadcast.ticker_text = payload.tickerText;
      broadcast.ticker_url = payload.tickerUrl;
      broadcast.show_ticker = true;
    }
  },
  stopSharingTicker(state: EventState, broadcastId: string) {
    const broadcast = state.event.agenda.broadcasts.find(b => b.id === broadcastId);
    if (broadcast) {
      broadcast.ticker_text = null;
      broadcast.ticker_url = null;
      broadcast.show_ticker = false;
    }
  },
  // errorStartingRecordingForBroadcast(state: EventState, broadcastID: string) {
  //   try {
  //     state.errorStartingRecordingBroadcastIDs = {
  //       ...state.errorStartingRecordingBroadcastIDs,
  //       [broadcastID]: true
  //     };
  //   } catch (err) {
  //     loggerService.error('Error setting recording broadcasts!', err);
  //   }
  // },
  // removeFromErrorStartingBroadcastList(state: EventState, broadcastID: string) {
  //   try {
  //     state.errorStartingRecordingBroadcastIDs[broadcastID] = false;
  //   } catch (err) {
  //     loggerService.error('Error setting recording broadcasts!', err);
  //   }
  // },
  setActivePeople(state: any, activePeople: any) {
    state.activePeople = activePeople;
  },
  setFromSplashTo(state: any, item: any) {
    state.fromSplashTo = item;
  },
  setTicketTypes(state: EventState, types: TicketType[]) {
    state.event.ticketTypes = types;
  },
  setActiveUsersCount(state: any, activeUsers: Object) {
    state.event = {
      ...state.event,
      activeUserCount: { ...state.event.activeUserCount, ...activeUsers }
    };
  },
  setActiveUsersCountUsingMmid(state: any, activeUsers: { [key: string]: number }) {
    const usersToExclude = activeUsers[MATTERMOST_DEFAULTS.EXCLUDED_ROOM_ID] || 0;
    activeUsers.ALL = activeUsers.all - usersToExclude;
    state.event.activeUserCountUsingMmid = activeUsers;
  },
  setIsVideoQNARequestLoading(state: EventState, payload: boolean) {
    state.isVideoQNARequestLoading = payload;
  },
  setSpeakerStageRequests(state: EventState, payload: SpeakerStageRequest[]) {
    state.stageControl = {
      ...state.stageControl,
      speakerStageRequests: payload
    };
  },
  setShouldForceMoveToStage(state: EventState, payload: boolean) {
    state.stageControl = {
      ...state.stageControl,
      shouldForceMoveToStage: payload
    };
  },
  setShouldKickFromStage(state: EventState, payload: boolean) {
    state.stageControl = {
      ...state.stageControl,
      shouldKickFromStage: payload
    };
  },
  setUseInHouseSimulive(state: EventState, payload: boolean) {
    state.useInHouseSimulive = payload;
  },
  setEdittedStyles(state: EventState, payload: Record<string, any>) {
    state.edittedStyles = payload;
  },
  setIsRecordingStatusOverlayVisible(state: EventState, payload: boolean) {
    state.isRecordingStatusOverlayVisible = payload;
  },
  setUseHlsGuard(state: EventState, payload: boolean) {
    state.useHlsGuard = payload;
  },
  setUseSpeakerActivity(state: EventState, payload: boolean) {
    state.useSpeakerActivity = payload;
  },
  setEventHeartbeatFastEnabled(state: EventState, payload: boolean) {
    state.isEventHeartbeatFastEnabled = payload;
  },
  setUseRecordingPlayPause(state: EventState, payload: boolean) {
    state.useRecordingPlayPause = payload;
  },
  setIsExitingRecording(state: EventState, payload: boolean) {
    state.isExitingRecording = payload;
  },
  setUseEventLocaleForDateTimeFormatting(state: EventState, payload: boolean) {
    state.useEventLocaleForDateTimeFormatting = payload;
  },
  setUsePentestFlow(state: EventState, payload: boolean) {
    state.usePentestFlow = payload;
  },
  setUseHideSidebar(state: EventState, payload: boolean) {
    state.useHideSidebar = payload;
  },
  setUse1080VideoQuality(state: EventState, payload: boolean) {
    state.use1080VideoQuality = payload;
  },
  setUseSkip480(state: EventState, payload: boolean) {
    state.useSkip480 = payload;
  },
  // Live Run of Show Cue Mutations
  setCues(state: EventState, cues: LiveRunOfShowCue[]) {
    state.liveRunOfShow.cues = cues.sort((a, b) => (a.score || 0) - (b.score || 0));
    state.liveRunOfShow.loading = false;
    state.liveRunOfShow.error = null;
    state.liveRunOfShow.lastFetch = Date.now();
  },

  setCueLoading(state: EventState, loading: boolean) {
    state.liveRunOfShow.loading = loading;
  },

  setCueError(state: EventState, error: string | null) {
    state.liveRunOfShow.error = error;
    state.liveRunOfShow.loading = false;
  },

  addCue(state: EventState, cue: LiveRunOfShowCue) {
    state.liveRunOfShow.cues.push(cue);
    state.liveRunOfShow.cues.sort((a, b) => (a.score || 0) - (b.score || 0));
  },

  updateCue(state: EventState, updatedCue: LiveRunOfShowCue) {
    const index = state.liveRunOfShow.cues.findIndex(cue => cue.id === updatedCue.id);
    if (index !== -1) {
      state.liveRunOfShow.cues[index] = { ...state.liveRunOfShow.cues[index], ...updatedCue };
    }
  },

  removeCue(state: EventState, cueId: string | number) {
    state.liveRunOfShow.cues = state.liveRunOfShow.cues.filter(cue => String(cue.id) !== String(cueId));
  },
  reorderCues(state: EventState, reorderedCues: LiveRunOfShowCue[]) {
    state.liveRunOfShow.cues = reorderedCues.sort((a, b) => (a.score || 0) - (b.score || 0));
  },
  toggleAllCuesExpanded(state: EventState) {
    state.liveRunOfShow.allCuesExpanded = !state.liveRunOfShow.allCuesExpanded;
  },
  setActiveCueId(state: EventState, cueId: string | null) {
    state.liveRunOfShow.activeCueId = cueId;
  },

  setEnableInhouseAddToCalendar(state: EventState, payload: boolean) {
    state.enableInhouseAddToCalendar = payload;
  },
  setUseCloudProxyFlow(state: EventState, payload: boolean) {
    state.useCloudProxyFlow = payload;
  },
  setUseCDNEndpoint(state: EventState, payload: boolean) {
    state.useCDNEndpoint = payload;
  },
  setUseNewChatWidget(state: EventState, payload: boolean) {
    state.useNewChatWidget = payload;
  },
  setUseNewNameCard(state: EventState, payload: boolean) {
    state.useNewNameCard = payload;
  },
  setDisableChatEmoji(state: EventState, payload: boolean) {
    state.disableChatEmoji = payload;
  },
  setUseSlowChat(state: EventState, payload: boolean) {
    state.useSlowChat = payload;
  },
  setEnablePushSpeakerToStage(state: EventState, payload: boolean) {
    state.enablePushSpeakerToStage = payload;
  },
  setEnableSpeakerReorder(state: EventState, payload: boolean) {
    state.enableSpeakerReorder = payload;
  },
  setEnableRemoveFromBackstage(state: EventState, payload: boolean) {
    state.enableRemoveFromBackstage = payload;
  },
  setUseGCAuth(state: EventState, payload: boolean) {
    state.useGCAuth = payload;
  },

  setUseBitrateCap(state: EventState, payload: number) {
    state.bitrateCap = payload;
  },
  setUseNewROSCues(state: EventState, payload: boolean) {
    state.useNewROSChanges = payload;
  },
  setUseOnDemandMQL(state: EventState, payload: boolean) {
    state.useOnDemandMQL = payload;
  },

  setUseNewSlideAnimation(state: EventState, payload: boolean) {
    state.useNewSlideAnimation = payload;
  },

  setUseFloatingEmoji(state: EventState, payload: boolean) {
    state.useFloatingEmoji = payload;
  },

  setUseNewAttendeeControlBar(state: EventState, payload: boolean) {
    state.useNewAttendeeControlBar = payload;
  },

  setUseCatchMeUp(state: EventState, payload: boolean) {
    state.useCatchMeUp = payload;
  },

  setCatchMeUpSummary(state: EventState, payload: AiSummaryStatusResponse | null) {
    state.catchMeUpSummary = payload;
  },
  setCatchMeUpGeneratedAt(state: EventState, generatedAt: string | null) {
    state.catchMeUpGeneratedAt = generatedAt;
  },
  setCatchMeUpRequestedTaskId(state: EventState, taskId: string | null) {
    state.catchMeUpRequestedTaskId = taskId;
  },
  setHasRequestedFullSummary(state: EventState, payload: boolean) {
    state.hasRequestedFullSummary = payload;
  },
  setCatchMeUpFeedbackSubmittedForTaskId(state: EventState, taskId: string | null) {
    state.catchMeUpFeedbackSubmittedForTaskId = taskId;
  },

  setHideFloatingEmojiReactions(state: EventState, payload: boolean) {
    state.hideFloatingEmojiReactions = payload;
  },

  setEnableBanAttendeeFromEvent(state: EventState, payload: boolean) {
    state.enableBanAttendeeFromEvent = payload;
  },
  setUseNewLayouts(state: EventState, payload: boolean) {
    state.useNewLayouts = payload;
  },
  setUseNewMuteUnmuteCapabilities(state: EventState, payload: boolean) {
    state.useNewMuteUnmuteCapabilities = payload;
  },
  setUsePlatformBrandKit(state: EventState, payload: boolean) {
    state.usePlatformBrandKit = payload;
  },
  setUseNewDenoiserV2(state: EventState, payload: boolean) {
    state.useNewDenoiserV2 = payload;
  },

  setUseRecorderSlides(state: EventState, payload: boolean) {
    state.useRecorderSlides = payload;
  },

  setEventApiConsolidation(state: EventState, payload: boolean) {
    state.useEventApiConsolidation = payload;
  },

  setMmApiConsolidation(state: EventState, payload: boolean) {
    state.useMmApiConsolidation = payload;
  },

  setUseAgoraDatadog(state: EventState, payload: boolean) {
    state.useAgoraDatadog = payload;
  },
  setRedirectUserToFailOverURL(state: EventState, payload: boolean) {
    state.redirectUserToFailOverURL = payload;
  },

  setEnableSessionLimit(state: EventState, payload: boolean) {
    state.enableSessionLimit = payload;
  },

  setUseAutoAudioRecover(state: EventState, payload: boolean) {
    state.useAutoAudioRecover = payload;
  },

  setUseOptimizeRSCloudRecording(state: EventState, payload: boolean) {
    state.useOptimizeRSCloudRecording = payload;
  },

  setUseRSNewLayouts(state: EventState, payload: boolean) {
    state.useRSNewLayouts = payload;
  },

  setUseRSAnimatedSlides(state: EventState, payload: boolean) {
    state.useRSAnimatedSlides = payload;
  },

  setNewSlideAnimationAssets(
    state: EventState,
    payload: {
      id: string | null;
      current_playing_slide_number: number;
      current_playing_step_number: number;
      slide_state_version: number;
      action?: SlideNavigationAction;
    }
  ) {
    state.new_slide_animation_assets = {
      id: payload.id,
      current_playing_slide_number: payload.current_playing_slide_number,
      current_playing_step_number: payload.current_playing_step_number,
      slide_state_version: payload.slide_state_version,
      action: payload.action
    };
  },

  updateNewSlideAnimationAssets(
    state: EventState,
    payload: Partial<{
      id: string | null;
      current_playing_slide_number: number;
      current_playing_step_number: number;
      slide_state_version: number;
      action: SlideNavigationAction;
    }>
  ) {
    if (state.new_slide_animation_assets) {
      state.new_slide_animation_assets = {
        ...state.new_slide_animation_assets,
        ...payload
      };
    }
  },

  clearNewSlideAnimationAssets(state: EventState) {
    state.new_slide_animation_assets = null;
  },

  updateBroadcastSlideState(
    state: EventState,
    payload: {
      broadcastId: string;
      slide_state_version: number;
      current_playing_slide_asset: string | null;
      current_playing_slide_number: number;
      current_playing_step_number: number;
    }
  ) {
    const broadcast = state.event.agenda.broadcasts.find(b => b.id === payload.broadcastId);
    if (broadcast) {
      broadcast.slide_state_version = payload.slide_state_version;
      broadcast.current_playing_slide_asset = payload.current_playing_slide_asset;
      broadcast.current_playing_slide_number = payload.current_playing_slide_number;
      broadcast.current_playing_step_number = payload.current_playing_step_number;
    }
  },
  updateTestingLayoutSettings(state: EventState, payload: Partial<EventState['testingLayoutSettings']>) {
    state.testingLayoutSettings = {
      ...state.testingLayoutSettings,
      ...payload
    };
  },
  updateCurrentBroadcastLayoutSettings(
    state: EventState,
    payload: { layoutSettings: LayoutSettings; resetState?: boolean }
  ) {
    state.updatedLayoutSettings = {
      ...state.updatedLayoutSettings,
      ...payload.layoutSettings
    };
    if (payload.resetState) {
      state.updatedLayoutSettings = {};
    }
  },
  updateIsSavingLayout(state: EventState, isSaving: boolean) {
    state.isSavingLayout = isSaving;
  },
  setSurveyIframe(state: EventState, payload: Partial<SurveyIframeState>) {
    Object.assign(state.surveyIframe, payload);
  },
  setCventIdentity(state: EventState, payload: { sessionId: string | null; accountMappingId: string | null }) {
    state.cventSessionId = payload.sessionId;
    state.cventAccountMappingId = payload.accountMappingId;
  }
};

export default mutations;
export type EventMutations = typeof mutations;
